<template>
    <div class="module-permissions p-6">
        <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <h2 class="text-xl font-semibold text-gray-800">Module Permissions</h2>
            <div class="w-full md:w-64">
                <div class="relative">
                    <input
                        v-model="searchQuery"
                        type="text"
                        placeholder="Search modules..."
                        class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
                        </svg>
                    </div>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-12">
            <div class="flex flex-col items-center">
                <svg class="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p class="mt-2 text-gray-600">Loading permissions...</p>
            </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div class="flex">
                <div class="flex-shrink-0">
                    <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                </div>
                <div class="ml-3">
                    <p class="text-sm text-red-700">{{ error }}</p>
                </div>
            </div>
        </div>

        <!-- No Modules Found -->
        <div v-else-if="filteredModules.length === 0" class="bg-white rounded-lg shadow p-6 text-center">
            <p class="text-gray-600">No modules found.</p>
        </div>

        <!-- Global Permissions Section -->
        <div v-if="!loading && globalPermissionTypes.length > 0" class="bg-white shadow overflow-hidden border-b border-gray-200 sm:rounded-lg mb-6">
            <div class="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 class="text-lg font-medium text-gray-900">Global Permissions</h3>
                <p class="mt-1 text-sm text-gray-500">These permissions apply across all modules</p>
            </div>
            <div class="px-6 py-4">
                <div class="flex flex-wrap gap-4">
                    <div 
                        v-for="permission in globalPermissionTypes" 
                        :key="permission.id"
                        class="flex items-center"
                    >
                        <input
                            :id="`global-${permission.name}`"
                            type="checkbox"
                            class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            :checked="hasGlobalPermission(permission.name)"
                            @change="updateGlobalPermission(permission.name, $event.target.checked)"
                            :disabled="saving"
                        />
                        <label :for="`global-${permission.name}`" class="ml-2 text-sm text-gray-700">
                            {{ permission.display_name || permission.name }}
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <!-- Module-Specific Permissions Table -->
        <div v-if="!loading && !error" class="bg-white shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-800">
                        <tr>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                Module
                            </th>
                            <th 
                                v-for="permission in permissionTypes" 
                                :key="permission.id"
                                scope="col" 
                                class="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider"
                                :title="permission.description"
                            >
                                {{ permission.display_name || permission.name }}
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                        <template v-if="permissionTypes.length === 0">
                            <tr>
                                <td colspan="100" class="px-6 py-4 text-center text-sm text-white">
                                    No permission types found
                                </td>
                            </tr>
                        </template>
                        <template v-else>
                            <tr v-for="module in filteredModules" :key="module.id" class="hover:bg-gray-50">
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm font-medium text-gray-900">{{ module.display_name || module.slug }}</div>
                                    <div v-if="module.description" class="text-xs text-gray-500">{{ module.description }}</div>
                                </td>
                                <td 
                                    v-for="permission in permissionTypes" 
                                    :key="permission.id"
                                    class="px-6 py-4 whitespace-nowrap text-center text-sm"
                                >
                                    <input
                                        type="checkbox"
                                        :checked="hasPermission(module.id, permission.name)"
                                        @change="updatePermission(module, permission.name, $event.target.checked)"
                                        class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                        :disabled="saving"
                                    />
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Save Button -->
        <div v-if="!loading && !error" class="mt-6 flex justify-between items-center">
            <div class="text-sm text-gray-500">
                Showing {{ filteredModules.length }} of {{ modules.length }} modules
            </div>
            <button
                @click="saveAllPermissions"
                :disabled="saving"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <svg v-if="saving" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ saving ? 'Saving...' : 'Save Changes' }}
            </button>
        </div>
    </div>
</template>

<script>
import { debounce } from 'lodash';

export default {
    name: 'ModulePermissions',
    props: {
        userId: {
            type: [String, Number],
            required: true
        },
        permissionGroup: {
            type: String,
            required: true,
            default: 'module'  // Default to 'module' if not specified
        }
    },
    data() {
        return {
            searchQuery: '',
            loading: false,
            saving: false,
            error: null,
            modules: [],
            permissionTypes: [],
            userPermissions: {},
            globalPermissions: [],
            debouncedSearch: null
        };
    },
    async created() {
        this.debouncedSearch = debounce(this.fetchData, 300);
        this.fetchData();
    },
    computed: {
        filteredModules() {
            if (!Array.isArray(this.modules)) {
                console.error('modules is not an array:', this.modules);
                return [];
            }
            
            const query = (this.searchQuery || '').toLowerCase().trim();
            if (!query) return this.modules;
            
            return this.modules.filter(module => {
                if (!module) return false;
                const name = (module.display_name || module.slug || '').toLowerCase();
                const description = (module.description || '').toLowerCase();
                return name.includes(query) || description.includes(query);
            });
        },
        
        globalPermissionTypes() {
            return this.permissionTypes.filter(pt => pt.is_global === true);
        },
        
        entityLevelPermissionTypes() {
            return this.permissionTypes.filter(pt => pt.is_global === false);
        }
    },
    watch: {
        searchQuery() {
            this.debouncedSearch();
        },
        userId: {
            immediate: true,
            handler() {
                this.fetchData();
            }
        }
    },
    methods: {
        hasPermission(moduleId, permissionName) {
            return this.userPermissions[moduleId]?.includes(permissionName) || false;
        },
        
        hasGlobalPermission(permissionName) {
            return this.globalPermissions.includes(permissionName);
        },
        
        updateGlobalPermission(permissionName, hasPermission) {
            if (hasPermission) {
                if (!this.globalPermissions.includes(permissionName)) {
                    this.globalPermissions.push(permissionName);
                }
            } else {
                const index = this.globalPermissions.indexOf(permissionName);
                if (index > -1) {
                    this.globalPermissions.splice(index, 1);
                }
            }
        },

        async fetchData() {
            this.loading = true;
            this.error = null;
            
            try {
                // Fetch accessible modules and permission types
                const [modulesRes, permissionsRes, typesRes] = await Promise.all([
                    axios.get('/api/user/accessible-modules'),
                    this.userId ? axios.get(`/api/users/${this.userId}/permissions/${this.permissionGroup}`) : Promise.resolve({ data: { data: [] } }),
                    axios.get('/api/permission-types', { params: { group: this.permissionGroup } })
                ]);
                
                // Handle API response structure - extract modules from the accessible modules response
                this.modules = (modulesRes.data?.data || []).map(item => item.module);
                
                // Set permission types from the API response
                this.permissionTypes = Array.isArray(typesRes.data) ? typesRes.data : (typesRes.data?.data || []);
                
                console.log('Accessible modules data:', this.modules);
                console.log('Permission types:', this.permissionTypes);
                
                // Process permissions response
                const responseData = permissionsRes.data?.data || {};
                const items = responseData.items || [];
                const groupData = responseData.group || {};
                
                // Initialize permissions
                this.userPermissions = {};
                this.globalPermissions = [];
                
                // Process global and module-specific permissions
                items.forEach(item => {
                    if (item.is_global && item.permissions) {
                        // Handle global permissions
                        Object.entries(item.permissions).forEach(([permName, isGranted]) => {
                            if (isGranted && !this.globalPermissions.includes(permName)) {
                                this.globalPermissions.push(permName);
                                console.log(`Added global permission: ${permName}`);
                            }
                        });
                    } else if (item.id && item.permissions) {
                        // Handle module-specific permissions
                        const modulePermissions = [];
                        const permissionTypes = groupData.permission_types || [];
                        
                        Object.entries(item.permissions).forEach(([permName, isGranted]) => {
                            if (isGranted) {
                                // Check if the permission is in the allowed types
                                const isValidPermission = permissionTypes.some(pt => {
                                    const typeName = typeof pt === 'object' ? pt.name : pt;
                                    return typeName === permName;
                                });
                                
                                if (isValidPermission) {
                                    modulePermissions.push(permName);
                                    console.log(`Added permission ${permName} for module ${item.id}`);
                                }
                            }
                        });
                        
                        console.log(`Final permissions for module ${item.id}:`, modulePermissions);
                        this.$set(this.userPermissions, item.id, modulePermissions);
                    }
                });
                
                console.log('Final userPermissions:', JSON.parse(JSON.stringify(this.userPermissions)));
                
                console.log('Processed userPermissions:', JSON.parse(JSON.stringify(this.userPermissions)));
                
                // Permission types are already set from the initial fetch
                
                console.log('Processed Permission Types:', this.permissionTypes);
                console.log('Permission Types Count:', this.permissionTypes.length);
                
            } catch (error) {
                console.error('Failed to fetch data:', error);
                this.error = 'Failed to load permissions. Please try again later.';
            } finally {
                this.loading = false;
            }
        },

        updatePermission(module, permissionName, hasPermission) {
            if (!this.userPermissions[module.id]) {
                this.$set(this.userPermissions, module.id, []);
            }

            if (hasPermission) {
                if (!this.userPermissions[module.id].includes(permissionName)) {
                    this.userPermissions[module.id].push(permissionName);
                }
            } else {
                this.userPermissions[module.id] = this.userPermissions[module.id].filter(
                    p => p !== permissionName
                );
            }
        },

        async saveAllPermissions() {
            this.saving = true;
            this.error = null;
            
            try {
                const promises = [];

                // Build and send complete permission state for EACH module (includes false to revoke)
                const entityTypes = this.entityLevelPermissionTypes;
                const entityTypeNames = entityTypes.map(pt => pt.name);

                this.modules.forEach((mod) => {
                    if (!mod || typeof mod.id === 'undefined') return;
                    const current = this.userPermissions[mod.id] || [];
                    const payload = {};
                    entityTypeNames.forEach((name) => {
                        payload[name] = current.includes(name);
                    });
                    promises.push(
                        axios.post(`/api/users/${this.userId}/permissions`, {
                            permissionable_type: this.permissionGroup,
                            permissionable_id: mod.id,
                            permissions: payload
                        })
                    );
                });

                // Always send global permissions as well (include false to revoke)
                if (this.globalPermissionTypes.length > 0) {
                    const globalPayload = {};
                    this.globalPermissionTypes.forEach(pt => {
                        globalPayload[pt.name] = this.globalPermissions.includes(pt.name);
                    });
                    promises.push(
                        axios.post(`/api/users/${this.userId}/permissions`, {
                            permissionable_type: this.permissionGroup,
                            permissionable_id: null,
                            permissions: globalPayload
                        })
                    );
                }

                await Promise.all(promises);

                this.$toast.success('Module permissions updated successfully');
            } catch (error) {
                console.error('Error saving module permissions:', error);
                this.error = 'Failed to save module permissions. Please try again.';
                this.$toast.error('Failed to save module permissions');
            } finally {
                this.saving = false;
            }
        }
    }
}
</script>
